import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEmbeddedApiClient } from '@/hooks/useEmbeddedApiClient';
import { useEmbeddedOrg } from '@/contexts/EmbeddedOrgContext';
import type { AcceptedPrice, BacklogWindow } from '@aica/api-types';
import type { ProcessingState, StartRecordsOptions } from './types';
import { useBacklogResultToast } from './useBacklogResultToast';
import { invalidateBacklogSummaries } from './invalidate-backlog-summaries';
import type { QuotedStart } from './useQuotedStart';
import { acceptedPriceOf, priceChangedQuote, quotedStartCallbacks } from './start-price';

/**
 * Состояние тумблера анализа внутри Bitrix24.
 *
 * Клиент вполне может не дойти до портала: весь первый час он живёт в
 * приложении CRM, и именно здесь должен увидеть, что приём работает, а
 * анализ ещё не запущен.
 *
 * Отдельный хук, а не ветка в `useProcessingState`: тот берёт клиента из
 * UnifiedAuthContext (Clerk), которого под `/embedded/*` нет. Пути
 * эндпоинтов те же — `useEmbeddedAuthFetch` внутри клиента сам переписывает
 * `/api/v1/*` → `/api/v1/embedded/*`.
 */
export function useEmbeddedProcessingState(): ProcessingState {
  const { apiClient } = useEmbeddedApiClient();
  const { orgId } = useEmbeddedOrg();
  const queryClient = useQueryClient();
  const backlogResult = useBacklogResultToast();

  const backlogKey = ['embedded', 'records', 'backlog-summary', orgId] as const;

  const backlogQuery = useQuery({
    queryKey: backlogKey,
    queryFn: () => apiClient.records.getBacklogSummary(),
    enabled: !!orgId,
    staleTime: 30 * 1000,
  });

  const backlogMutation = useMutation({
    mutationFn: ({
      range,
      acceptedPrice,
    }: {
      range?: BacklogWindow;
      acceptedPrice: AcceptedPrice;
    }) => apiClient.records.processBacklog(range, acceptedPrice),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: backlogKey });
      void queryClient.invalidateQueries({ queryKey: ['embedded', 'records'] });
      backlogResult.onSuccess(result);
    },
    // Цена партии выросла, пока окно было открыто (#1396): ничего не
    // запущено. Сводка перечитывается — карточка покажет новую цену, — а тост
    // объясняет, почему подтверждение не сработало.
    onError: (error) => {
      if (priceChangedQuote(error)) {
        invalidateBacklogSummaries(queryClient);
        backlogResult.onPriceChanged();
        return;
      }
      backlogResult.onError(error);
    },
  });

  // Ручной запуск ВЫБРАННЫХ записей. Ключ инвалидации — как у backlogMutation
  // выше (`['embedded', 'records']`), а не портальный `['records']`: у
  // встроенного режима свой префикс query-ключей.
  const startMutation = useMutation({
    mutationFn: ({ recordIds, quote }: QuotedStart) =>
      apiClient.records.startRecords(recordIds, acceptedPriceOf(quote)),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: backlogKey });
      void queryClient.invalidateQueries({ queryKey: ['embedded', 'records'] });
      backlogResult.onStartSuccess(result);
    },
    // Отказ «цена выросла» объявляет вызывающий — когда показывает новую цену.
    // Здесь только сводки накопленного: цену им считает тот же грант, и
    // карточка со старой ценой вела бы ко второму отказу (code review r2 на
    // #1454).
    onError: (error) => {
      if (priceChangedQuote(error)) {
        invalidateBacklogSummaries(queryClient);
        return;
      }
      backlogResult.onError(error);
    },
  });

  // Обёрнут в useCallback: этот метод попадает в deps-массив useMemo/props на
  // стороне потребителя (RecordDetailInline внутри RecordsTab — тяжёлая
  // карточка) и обязан быть стабильным между рендерами родителя, симметрично
  // портальному useProcessingState. `startMutation.mutate` сам по себе уже
  // стабилен (React Query держит его на persistent observer'е) — useCallback
  // сохраняет эту стабильность и для обёртки вокруг него.
  // `mutate` вынесен в отдельную переменную (не `startMutation.mutate`
  // инлайном) — react-hooks/exhaustive-deps не сужает вызов МЕТОДА объекта в
  // зависимостях до самого метода и просит весь `startMutation` целиком; тот
  // — новый объект на каждый рендер (`useMutation` возвращает свежий result),
  // что свело бы на нет стабильность, которую даёт useCallback.
  const { mutate: startMutate } = startMutation;
  // Принимает только набор, по которому показана цена (`QuotedStart` —
  // его создаёт лишь useQuotedStart, #1389).
  //
  // Успех и отказ «цена выросла» (#1396) отдаются вызывающему: выбор
  // снимается только по факту запуска, а при отказе диалог открывается с
  // новой ценой (`quotedStartCallbacks`).
  const startRecords = useCallback(
    (quoted: QuotedStart, options?: StartRecordsOptions) =>
      startMutate(quoted, quotedStartCallbacks(options)),
    [startMutate]
  );

  // Переключатель режима пишет то же поле настроек, что и раньше
  // (`processingEnabled`) — меняется его прочтение (авто/ручной), а не
  // хранение. Раньше это была ОТДЕЛЬНАЯ мутация (`enableMutation`) под
  // кнопку баннера «включить анализ»; когда баннер стал звать `setMode`, две
  // параллельные мутации на одно и то же поле оставляли `isEnabling`
  // привязанным к всегда-пустой `enableMutation` — кнопка переставала
  // блокироваться на время реального запроса.
  const modeMutation = useMutation({
    mutationFn: (mode: 'auto' | 'manual') =>
      apiClient.settings.updateSettings({ processingEnabled: mode === 'auto' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: backlogKey });
    },
    // Без обработчика отказ мутации проходил молча: TanStack Query откатывает
    // радиокнопку/тумблер обратно, а человек не видит, почему выбор не удержался.
    onError: backlogResult.onModeError,
  });

  // Стабильность, тем же приёмом, что у `startRecords` выше — симметрично
  // портальному useProcessingState (Task B4).
  const { mutate: setModeMutate } = modeMutation;
  const setMode = useCallback(
    (mode: 'auto' | 'manual') => setModeMutate(mode),
    [setModeMutate]
  );
  const quoteStart = useCallback(
    (recordIds: string[]) => apiClient.records.quoteStart(recordIds),
    [apiClient]
  );

  // Флаг и право приходят вместе со сводкой: отдельный запрос к /settings
  // требовал user.read (client/admin) и отдавал 403 супервизорам и агентам.
  // Дефолт true до загрузки — чтобы баннер не мигал у тех, у кого анализ включён.
  return {
    enabled: backlogQuery.data?.processingEnabled !== false,
    canStart: backlogQuery.data?.canStart ?? false,
    summary: backlogQuery.data ?? null,
    fetchRangeSummary: (range: { from: string }) =>
      apiClient.records.getBacklogSummary(range),
    loading: backlogQuery.isLoading,
    error: backlogQuery.isError,
    isEnabling: modeMutation.isPending,
    isProcessingBacklog: backlogMutation.isPending,
    isStarting: startMutation.isPending,
    processBacklog: (range, acceptedPrice) =>
      backlogMutation.mutate({ range, acceptedPrice }),
    startRecords,
    quoteStart,
    setMode,
  };
}
